import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Bell, X, ChatCircle, CurrencyDollar, WarningOctagon, ShieldCheck, ArrowsCounterClockwise, Briefcase, Flag } from '@phosphor-icons/react';

const NotifContext = createContext(null);

const SOUND_FREQ = [523.25, 659.25, 783.99]; // C5-E5-G5 chord

function playNotificationSound(priority) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const isCritical = priority === 'critical';
    const isHigh = priority === 'high' || isCritical;
    const volume = isCritical ? 0.5 : isHigh ? 0.35 : 0.2;
    const freqs = isCritical ? [523.25, 659.25, 783.99, 1046.5] : SOUND_FREQ;
    freqs.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = isCritical ? 'triangle' : 'sine';
      osc.frequency.setValueAtTime(freq, ctx.currentTime);
      gain.gain.setValueAtTime(volume, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + (isCritical ? 1.2 : 0.8));
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime + i * 0.1);
      osc.stop(ctx.currentTime + (isCritical ? 1.5 : 1));
    });
    // Second chime
    setTimeout(() => {
      try {
        const ctx2 = new (window.AudioContext || window.webkitAudioContext)();
        const freqs2 = isCritical ? [1046.5, 1318.5, 1046.5] : [783.99, 987.77];
        freqs2.forEach((freq, i) => {
          const osc = ctx2.createOscillator();
          const gain = ctx2.createGain();
          osc.type = isCritical ? 'triangle' : 'sine';
          osc.frequency.setValueAtTime(freq, ctx2.currentTime);
          gain.gain.setValueAtTime(volume * 0.8, ctx2.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.01, ctx2.currentTime + 0.6);
          osc.connect(gain);
          gain.connect(ctx2.destination);
          osc.start(ctx2.currentTime + i * 0.12);
          osc.stop(ctx2.currentTime + 0.7);
        });
      } catch {}
    }, isCritical ? 300 : 400);
    // Critical: third chime
    if (isCritical) {
      setTimeout(() => {
        try {
          const ctx3 = new (window.AudioContext || window.webkitAudioContext)();
          [1568, 1318.5, 1046.5].forEach((freq, i) => {
            const osc = ctx3.createOscillator();
            const gain = ctx3.createGain();
            osc.type = 'triangle';
            osc.frequency.setValueAtTime(freq, ctx3.currentTime);
            gain.gain.setValueAtTime(0.3, ctx3.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.01, ctx3.currentTime + 0.5);
            osc.connect(gain);
            gain.connect(ctx3.destination);
            osc.start(ctx3.currentTime + i * 0.15);
            osc.stop(ctx3.currentTime + 0.6);
          });
        } catch {}
      }, 700);
    }
  } catch {}
}

function flashScreen(priority) {
  const isCritical = priority === 'critical';
  const overlay = document.createElement('div');
  const color = isCritical ? 'rgba(232,106,51,0.45)' : 'rgba(232,106,51,0.2)';
  const duration = isCritical ? '1s' : '0.6s';
  overlay.style.cssText = `position:fixed;inset:0;z-index:99999;pointer-events:none;background:${color};animation:notifFlash ${duration} ease-out forwards`;
  document.body.appendChild(overlay);
  setTimeout(() => overlay.remove(), isCritical ? 1100 : 700);
  // Critical: double flash
  if (isCritical) {
    setTimeout(() => {
      const overlay2 = document.createElement('div');
      overlay2.style.cssText = `position:fixed;inset:0;z-index:99999;pointer-events:none;background:rgba(232,106,51,0.3);animation:notifFlash 0.5s ease-out forwards`;
      document.body.appendChild(overlay2);
      setTimeout(() => overlay2.remove(), 600);
    }, 500);
  }
}

const ICON_MAP = {
  counter_offer: ArrowsCounterClockwise, dispute: WarningOctagon, dispute_resolved: ShieldCheck,
  payment: CurrencyDollar, chat: ChatCircle, job_match: Briefcase, milestone: Flag,
  milestone_approved: Flag, bid_received: Bell, bid_accepted: Bell, bid_rejected: Bell,
  escrow_funded: CurrencyDollar, payment_released: CurrencyDollar, job_completed: ShieldCheck,
  rating_received: Bell, default: Bell,
};

const BG_MAP = {
  counter_offer: 'from-[#F4A261] to-[#E86A33]', dispute: 'from-red-500 to-red-600',
  dispute_resolved: 'from-[#2D6A4F] to-[#1A4D2E]', payment: 'from-[#E86A33] to-[#D35A29]',
  job_match: 'from-[#1A4D2E] to-[#2D6A4F]', milestone: 'from-[#F4A261] to-[#E86A33]',
  milestone_approved: 'from-[#2D6A4F] to-[#1A4D2E]', bid_received: 'from-[#1A4D2E] to-[#2D6A4F]',
  bid_accepted: 'from-[#2D6A4F] to-[#1A4D2E]', escrow_funded: 'from-[#2D6A4F] to-[#1A4D2E]',
  payment_released: 'from-[#2D6A4F] to-[#1A4D2E]', job_completed: 'from-[#2D6A4F] to-[#1A4D2E]',
  rating_received: 'from-[#F4A261] to-[#E86A33]', chat: 'from-[#1A4D2E] to-[#133922]',
  default: 'from-[#1A4D2E] to-[#133922]',
};

export function NotificationProvider({ children }) {
  const { user, token, api } = useAuth();
  const [toasts, setToasts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [prefs, setPrefs] = useState(null);
  const lastCheckRef = useRef(null);
  const seenIdsRef = useRef(new Set());
  const pushAskedRef = useRef(false);

  // Load prefs
  useEffect(() => {
    if (!user) return;
    api.get('/users/notification-prefs').then(({ data }) => setPrefs(data.prefs)).catch(() => {});
  }, [user, api]);

  // Auto-subscribe to push notifications when user logs in
  useEffect(() => {
    if (!user || !token || pushAskedRef.current) return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    pushAskedRef.current = true;

    (async () => {
      try {
        if (Notification.permission === 'granted') {
          const registration = await navigator.serviceWorker.register('/push-sw.js');
          await navigator.serviceWorker.ready;
          const { data } = await api.get('/push/vapid-key');
          if (!data.publicKey) return;
          const padding = '='.repeat((4 - data.publicKey.length % 4) % 4);
          const base64 = (data.publicKey + padding).replace(/-/g, '+').replace(/_/g, '/');
          const rawData = atob(base64);
          const applicationServerKey = Uint8Array.from([...rawData].map(ch => ch.charCodeAt(0)));
          const subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
          await api.post('/push/subscribe', subscription.toJSON());
        }
      } catch (e) { /* Push setup optional */ }
    })();
  }, [user, token, api]);

  const isQuietHours = () => {
    if (!prefs?.quiet_hours_enabled) return false;
    const now = new Date();
    const h = now.getHours(), m = now.getMinutes();
    const [sh, sm] = (prefs.quiet_start || '22:00').split(':').map(Number);
    const [eh, em] = (prefs.quiet_end || '07:00').split(':').map(Number);
    const curr = h * 60 + m, start = sh * 60 + sm, end = eh * 60 + em;
    if (start > end) return curr >= start || curr < end;
    return curr >= start && curr < end;
  };

  const addToast = useCallback((notif) => {
    const id = notif.id || Date.now().toString();
    if (seenIdsRef.current.has(id)) return;
    seenIdsRef.current.add(id);
    // Check if user disabled this type
    if (prefs && notif.type && prefs[notif.type] === false && notif.priority !== 'critical') return;
    setToasts(prev => [...prev.slice(-4), { ...notif, toastId: id }]);
    const quiet = isQuietHours();
    // Critical always plays sound, others respect prefs
    if (notif.priority === 'critical' || (!quiet && prefs?.sound_enabled !== false)) {
      playNotificationSound(notif.priority || 'normal');
    }
    if (notif.priority === 'critical' || (!quiet && prefs?.flash_enabled !== false)) {
      flashScreen(notif.priority || 'normal');
    }
    const duration = notif.priority === 'critical' ? 10000 : notif.priority === 'high' ? 7000 : 5000;
    setTimeout(() => setToasts(prev => prev.filter(t => t.toastId !== id)), duration);
  }, [prefs]);

  const dismissToast = (toastId) => setToasts(prev => prev.filter(t => t.toastId !== toastId));

  // Poll for new notifications
  useEffect(() => {
    if (!user) return;
    const poll = async () => {
      try {
        const { data } = await api.get('/notifications');
        setUnreadCount(data.unread_count || 0);
        // Flash new unread ones
        const notifs = data.notifications || [];
        for (const n of notifs) {
          if (!n.read && !seenIdsRef.current.has(n.id)) {
            addToast(n);
          }
        }
      } catch {}
    };
    poll();
    const interval = setInterval(poll, 8000);
    return () => clearInterval(interval);
  }, [user, api, addToast]);

  return (
    <NotifContext.Provider value={{ unreadCount, addToast }}>
      {children}
      {/* Toast Container */}
      <div className="fixed top-4 right-4 left-4 z-[9998] flex flex-col items-center gap-2 max-w-[420px] mx-auto pointer-events-none" data-testid="toast-container">
        {toasts.map(toast => {
          const Icon = ICON_MAP[toast.type] || ICON_MAP.default;
          const bg = BG_MAP[toast.type] || BG_MAP.default;
          return (
            <div key={toast.toastId} className={`w-full bg-gradient-to-r ${bg} rounded-xl p-4 shadow-2xl shadow-black/20 pointer-events-auto animate-in`} data-testid={`toast-${toast.toastId}`}>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Icon size={18} className="text-white" weight="bold" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="text-sm font-bold text-white font-body">{toast.title}</h4>
                    {toast.priority === 'critical' && <span className="text-[9px] font-black bg-white/30 text-white px-1.5 py-0.5 rounded uppercase tracking-wider animate-pulse">Urgent</span>}
                  </div>
                  <p className="text-xs text-white/80 font-body truncate mt-0.5">{toast.message}</p>
                </div>
                <button onClick={() => dismissToast(toast.toastId)} className="text-white/60 hover:text-white flex-shrink-0 pointer-events-auto">
                  <X size={16} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </NotifContext.Provider>
  );
}

export const useNotifications = () => useContext(NotifContext);
